import { useEffect, useState } from "react";
import { Target, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { trackMetaEvent } from "@/lib/metaPixel";

const GOALS = [
  "Prepare for first job",
  "Prepare for first bank account",
  "Prepare for credit card",
  "Prepare for college decisions",
  "Build entrepreneurship mindset",
  "Improve investing confidence",
  "Strengthen budgeting discipline",
];

interface Props { childId: string | null; }

export default function ParentGoalsPicker({ childId }: Props) {
  const { user } = useAuth();
  const storageKey = user && childId ? `hw_parent_goals_${user.id}_${childId}` : null;
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      setSelected(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setSelected([]);
    }
  }, [storageKey]);

  const toggle = (goal: string) => {
    if (!storageKey) return;
    const next = selected.includes(goal) ? selected.filter((g) => g !== goal) : [...selected, goal];
    setSelected(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
      void trackMetaEvent("child_goal_updated", { customData: { goal, action: selected.includes(goal) ? "removed" : "added" } });
    } catch {
      toast.error("Couldn't save on this device.");
    }
  };

  return (
    <div className="rounded-2xl border border-white/[0.05] bg-background/30 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Target className="h-3.5 w-3.5 text-primary" />
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Parent goals for this teen</p>
        </div>
        <span className="text-[10px] italic text-muted-foreground">Saved on this device</span>
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
        Pick the outcomes you care about most. HeirWise uses these to tailor the Parent Intelligence Brief and the Action Loop as more learning data comes in. Nothing here makes claims about your teen — goals only shape what HeirWise highlights for you.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {GOALS.map((g) => {
          const active = selected.includes(g);
          return (
            <button
              type="button"
              key={g}
              onClick={() => toggle(g)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition",
                active
                  ? "border-primary/40 bg-primary/15 text-primary"
                  : "border-white/10 bg-background/40 text-muted-foreground hover:text-foreground"
              )}
              disabled={!childId}
            >
              {active && <Check className="h-3 w-3" />}
              {g}
            </button>
          );
        })}
      </div>
      {!childId && (
        <p className="mt-3 text-[11px] italic text-muted-foreground">Add a child to set parent goals.</p>
      )}
      {childId && selected.length === 0 && (
        <p className="mt-3 text-[11px] italic text-muted-foreground">No goals selected — HeirWise will surface the most universal next steps.</p>
      )}
    </div>
  );
}